// Env flavors for the Calls hub: local (dev), prod (canonical domains), or auto.
type AppEnv = 'local' | 'prod' | 'auto'

// Source of truth for the current flavor; default to auto to avoid accidental localhost on prod.
const APP_ENV: AppEnv = (process.env.NEXT_PUBLIC_APP_ENV as AppEnv) || 'auto'

// Local dashboard URLs for non-prod runs (keeps testing isolated).
const LOCAL_TEACHER_URL =
  process.env.NEXT_PUBLIC_TEACHER_URL_LOCAL || 'http://localhost:5176'
const LOCAL_STUDENT_URL =
  process.env.NEXT_PUBLIC_STUDENT_URL_LOCAL || 'http://localhost:5173'

// Prod dashboard hosts; configurable per region with sensible defaults.
const DASHBOARD_HOST_US =
  process.env.NEXT_PUBLIC_DASHBOARD_HOST_US || 'subschool.us'
const DASHBOARD_HOST_RU =
  process.env.NEXT_PUBLIC_DASHBOARD_HOST_RU || 'subschool.ru'
const PROD_TEACHER_URL =
  process.env.NEXT_PUBLIC_TEACHER_URL_PROD || ''
const PROD_STUDENT_URL =
  process.env.NEXT_PUBLIC_STUDENT_URL_PROD || ''

// Decide if the current hostname is local; used to keep localhost workflows intact.
function isLocalHost(host: string): boolean {
  const normalized = host.toLowerCase().trim()
  return (
    normalized === 'localhost' ||
    normalized === '127.0.0.1' ||
    normalized === '0.0.0.0'
  )
}

// Decide runtime mode; auto uses the current hostname when possible.
function resolveRuntime(): 'local' | 'prod' {
  // Explicit override wins for reproducibility.
  if (APP_ENV === 'local') return 'local'
  if (APP_ENV === 'prod') return 'prod'
  // Auto: infer from browser host; default to prod on the server.
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || ''
    if (isLocalHost(host)) return 'local'
    return 'prod'
  }
  return 'prod'
}

// Decide region automatically from browser hostname; .ru domains map to RU.
function resolveRegion(): 'us' | 'ru' {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || ''
    if (host.endsWith('.ru')) return 'ru'
  }
  return 'us'
}

// Build teacher/student dashboard URLs for CTA buttons.
// - Non-prod: always point to local URLs to avoid hitting prod while testing.
// - Prod: choose region explicitly or infer from current hostname.
// Build teacher/student dashboard URLs for CTA buttons.
// - Local runtime: always point to local URLs.
// - Prod runtime: select .ru or .us based on current host (or explicit region).
export function resolveDashboardUrls(region?: 'us' | 'ru'): {
  teacherUrl: string
  studentUrl: string
} {
  // Keep localhost flows deterministic in dev and when Calls is opened on localhost.
  if (resolveRuntime() !== 'prod') {
    return { teacherUrl: LOCAL_TEACHER_URL, studentUrl: LOCAL_STUDENT_URL }
  }
  if (PROD_TEACHER_URL && PROD_STUDENT_URL) {
    return { teacherUrl: PROD_TEACHER_URL, studentUrl: PROD_STUDENT_URL }
  }
  const targetRegion = region || resolveRegion()
  const host = targetRegion === 'ru' ? DASHBOARD_HOST_RU : DASHBOARD_HOST_US
  return {
    teacherUrl: `https://teacher.${host}`,
    studentUrl: `https://class.${host}`,
  }
}

// Convenience flag for callers.
export const IS_PROD = APP_ENV === 'prod'
